import React, { useEffect, useRef, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import {
  ArrowRight, ArrowLeft, Check, User, Building2, Upload, Palette, Sparkles,
  ShieldCheck, Loader2, Crown, Phone, Mail, Image as ImageIcon, Link2, Home, TrendingUp, Lock, Rocket,
} from "lucide-react";
import Logo from "../components/Logo";
import CityAutocomplete from "../components/CityAutocomplete";
import { useAuth, formatApiError } from "../lib/auth";
import { toast } from "sonner";

const STEPS = [
  { key: "business", title: "Tell us about your business.", subtitle: "Let's personalize Newground for you." },
  { key: "brand", title: "Make it yours.", subtitle: "Your branding will automatically appear on every advertisement." },
  { key: "connect", title: "Connect your Facebook & Instagram.", subtitle: "Connect your advertising account to launch campaigns directly from Newground." },
  { key: "campaign", title: "Make your first campaign.", subtitle: "Turn a listing into a ready-to-run ad — we'll take it from here." },
];

const ACCOUNT_TYPES = [
  { value: "individual", label: "Individual Agent" },
  { value: "team", label: "Team Leader" },
  { value: "brokerage", label: "Brokerage Owner" },
];

const COLOR_PRESETS = [
  { primary: "#1D6AFF", accent: "#F59E0B", name: "Newground" },
  { primary: "#0B3FA3", accent: "#22C55E", name: "Trust" },
  { primary: "#9333EA", accent: "#F97316", name: "Bold" },
  { primary: "#EF4444", accent: "#F5C518", name: "Power" },
  { primary: "#0F766E", accent: "#FB923C", name: "Coastal" },
  { primary: "#111827", accent: "#FACC15", name: "Luxury" },
];

function readFileAsDataUrl(file, maxDim = 1024) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        const scale = Math.min(1, maxDim / Math.max(width, height));
        width = Math.round(width * scale); height = Math.round(height * scale);
        const c = document.createElement("canvas");
        c.width = width; c.height = height;
        c.getContext("2d").drawImage(img, 0, 0, width, height);
        resolve(c.toDataURL("image/jpeg", 0.85));
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

export default function Onboarding() {
  const navigate = useNavigate();
  const { user, hydrated, updateUser, refresh } = useAuth();
  const [step, setStep] = useState(0);
  const [showWelcome, setShowWelcome] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const [objective, setObjective] = useState("listing"); // "listing" | "seller"
  const [listingUrl, setListingUrl] = useState("");
  const [manualListing, setManualListing] = useState(false);
  const [showMetaGate, setShowMetaGate] = useState(false);

  const [form, setForm] = useState({
    name: "", account_type: "individual", brokerage_name: "",
    target_city: "", target_market: "Residential: Buyer & Seller",
    headshot: null, brand_logo: null,
    business_phone: "", business_email: "",
    brand_color_primary: "#1D6AFF", brand_color_accent: "#F59E0B",
  });

  useEffect(() => {
    if (user) {
      setForm((f) => ({
        ...f,
        name: user.name || "",
        account_type: user.account_type || "individual",
        brokerage_name: user.brokerage_name || user.agency || "",
        target_city: user.target_city || "",
        target_market: user.target_market || f.target_market,
        headshot: user.headshot || user.avatar || null,
        brand_logo: user.brand_logo || null,
        business_phone: user.business_phone || user.phone || "",
        business_email: user.business_email || user.email || "",
        brand_color_primary: user.brand_color_primary || "#1D6AFF",
        brand_color_accent: user.brand_color_accent || "#F59E0B",
      }));
      if (typeof user.onboarding_step === "number" && user.onboarding_step > 0) {
        setStep(Math.min(user.onboarding_step, STEPS.length - 1));
        setShowWelcome(false);
      }
    }
  }, [user]);

  if (!hydrated) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.plan === "free" || !user.plan) return <Navigate to="/dashboard/plans" replace />;
  if (user.onboarding_completed) return <Navigate to="/dashboard" replace />;

  const isElite = user.plan === "elite";
  const metaConnected = !!user.integrations?.meta;

  const persist = async (patch, nextStep) => {
    setBusy(true); setError(null);
    try {
      await updateUser({ ...patch, ...(nextStep != null ? { onboarding_step: nextStep } : {}) });
      return true;
    } catch (e) {
      setError(formatApiError(e, "Could not save. Try again."));
      return false;
    } finally { setBusy(false); }
  };

  const brandPatch = () => ({
    name: form.name.trim(),
    account_type: form.account_type,
    brokerage_name: form.brokerage_name.trim(),
    agency: form.brokerage_name.trim(),
    target_city: form.target_city.trim(),
    target_market: form.target_market,
    headshot: form.headshot,
    brand_logo: form.brand_logo,
    business_phone: form.business_phone.trim(),
    business_email: form.business_email.trim(),
    brand_color_primary: form.brand_color_primary,
    brand_color_accent: form.brand_color_accent,
  });

  const validateBusiness = () => {
    if (!form.name.trim()) { setError("Full name is required"); return false; }
    if (!form.target_city.trim()) { setError("Primary market (city & state) is required"); return false; }
    setError(null); return true;
  };

  const goNext = async () => {
    if (step === 0 && !validateBusiness()) return;
    const ok = await persist(brandPatch(), step + 1);
    if (ok) setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };

  const goBack = () => { setError(null); setStep((s) => Math.max(0, s - 1)); };

  const doHandoff = async (asDraft) => {
    const ok = await persist({ ...brandPatch(), onboarding_completed: true, onboarding_objective: objective, onboarding_step: STEPS.length }, null);
    if (!ok) return;
    await refresh();
    if (objective === "listing") {
      navigate("/dashboard/creative", { replace: true, state: { importUrl: listingUrl.trim() || undefined, manual: manualListing, publishIntent: !asDraft } });
    } else {
      navigate("/dashboard/leads", { replace: true, state: { focus: "seller", publishIntent: !asDraft } });
    }
  };

  const completeAndGo = () => {
    if (!objective) { setError("Pick a campaign to continue, or choose \"I'll do this later\"."); return; }
    if (objective === "seller" && !isElite) { setError("Seller campaigns are an Elite feature. Upgrade to Elite from your dashboard to unlock them, or choose Promote a Listing."); return; }
    if (objective === "listing" && !listingUrl.trim() && !manualListing) { setError("Paste your listing URL, or choose \"enter details manually\"."); return; }
    setError(null);
    if (metaConnected) { doHandoff(false); }   // Meta ready → go all the way to publishing
    else { setShowMetaGate(true); }            // not connected → show connect-first message
  };

  const skipToDashboard = async () => {
    const ok = await persist({ ...brandPatch(), onboarding_completed: true }, null);
    if (!ok) return;
    await refresh();
    toast.success("You're all set", { description: "Finish setup anytime from the checklist on your dashboard." });
    navigate("/dashboard", { replace: true });
  };

  // ── Welcome screen ────────────────────────────────────────────────
  if (showWelcome) {
    return (
      <Shell user={user}>
        <div className="relative bg-white border border-slate-200 rounded-3xl p-8 sm:p-12 shadow-[0_30px_80px_-30px_rgba(15,23,42,0.18)] overflow-hidden text-center" data-testid="onboarding-welcome">
          <div className="absolute -top-24 -right-24 w-72 h-72 bg-[#1D6AFF]/8 rounded-full blur-3xl pointer-events-none" />
          <div className="relative">
            <div className="w-16 h-16 mx-auto rounded-2xl bg-[#1D6AFF]/10 border border-[#1D6AFF]/25 flex items-center justify-center mb-6">
              <Rocket className="w-8 h-8 text-[#1D6AFF]" />
            </div>
            <div className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1 mb-5">
              <Check className="w-3 h-3" /> Payment confirmed
            </div>
            <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mb-3">Welcome to Newground, {form.name?.split(" ")[0] || "there"}.</h1>
            <p className="text-base text-slate-500 max-w-md mx-auto mb-8">Let's set up your workspace. It takes about 2 minutes — your details are saved as you go, so you can leave and pick up anytime.</p>
            <button data-testid="welcome-start" onClick={() => setShowWelcome(false)}
              className="inline-flex items-center gap-2 bg-[#1D6AFF] hover:bg-[#1552C6] text-white font-semibold rounded-xl px-7 py-3.5 transition-all shadow-[0_14px_30px_-8px_rgba(29,106,255,0.45)] hover:-translate-y-0.5">
              Set up my workspace <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </Shell>
    );
  }

  return (
    <Shell user={user}>
      {/* Stepper */}
      <div className="flex items-center justify-between mb-9 max-w-lg mx-auto">
        {STEPS.map((s, i) => (
          <React.Fragment key={s.key}>
            <div className="flex flex-col items-center gap-2" data-testid={`step-indicator-${i}`}>
              <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold border transition-all ${
                i < step ? "bg-emerald-500 border-emerald-500 text-white"
                : i === step ? "bg-[#1D6AFF] text-white border-[#1D6AFF] shadow-[0_0_0_6px_rgba(29,106,255,0.12)]"
                : "bg-white border-slate-200 text-slate-400"}`}>
                {i < step ? <Check className="w-4 h-4" /> : i + 1}
              </div>
              <div className={`text-[10px] uppercase tracking-wider font-bold ${i <= step ? "text-slate-700" : "text-slate-400"}`}>{s.key}</div>
            </div>
            {i < STEPS.length - 1 && <div className={`flex-1 h-px mx-2 transition-colors ${i < step ? "bg-emerald-400" : "bg-slate-200"}`} />}
          </React.Fragment>
        ))}
      </div>

      <div className="relative bg-white border border-slate-200 rounded-3xl p-7 sm:p-9 shadow-[0_30px_80px_-30px_rgba(15,23,42,0.18)] overflow-hidden">
        <div className="absolute -top-24 -right-24 w-72 h-72 bg-[#1D6AFF]/8 rounded-full blur-3xl pointer-events-none" />
        <div className="relative">
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 mb-1.5">Step {step + 1} of {STEPS.length}</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold mb-2 tracking-tight text-slate-900">{STEPS[step].title}</h1>
          <p className="text-sm text-slate-500 mb-7 leading-relaxed">{STEPS[step].subtitle}</p>

          {step === 0 && <StepBusiness form={form} setForm={setForm} />}
          {step === 1 && <StepBrand form={form} setForm={setForm} />}
          {step === 2 && <StepConnect />}
          {step === 3 && (
            <>
              <StepCampaign
                isElite={isElite} objective={objective} setObjective={setObjective}
                listingUrl={listingUrl} setListingUrl={setListingUrl}
                manualListing={manualListing} setManualListing={setManualListing}
                metaConnected={metaConnected}
              />
              {showMetaGate && (
                <div className="mt-5 rounded-2xl border border-amber-200 bg-amber-50 p-5" data-testid="meta-gate">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-amber-100 border border-amber-200 flex items-center justify-center shrink-0">
                      <ShieldCheck className="w-4.5 h-4.5 text-amber-600" />
                    </div>
                    <div className="flex-1">
                      <div className="font-semibold text-slate-900 text-sm mb-1">Connect Meta to publish</div>
                      <p className="text-sm text-slate-600 leading-relaxed mb-3">
                        You'll need a connected Meta account before this campaign can go live. You can build your ad now and save it as a draft — publish the moment Meta is connected.
                      </p>
                      <div className="flex flex-wrap items-center gap-2.5">
                        <button data-testid="metagate-connect" onClick={() => { setShowMetaGate(false); setError(null); setStep(2); }}
                          className="inline-flex items-center gap-1.5 bg-[#1877F2] hover:bg-[#1466d6] text-white text-sm font-semibold rounded-xl px-4 py-2.5 transition-all">
                          Connect Meta first
                        </button>
                        <button data-testid="metagate-draft" disabled={busy} onClick={() => doHandoff(true)}
                          className="inline-flex items-center gap-1.5 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-semibold rounded-xl px-4 py-2.5 transition-all">
                          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Create as draft anyway <ArrowRight className="w-4 h-4" /></>}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {error && <div className="mt-5 text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-4 py-3" data-testid="onboarding-error">{error}</div>}

          <div className="flex items-center justify-between mt-8 pt-6 border-t border-slate-100">
            <button data-testid="onboarding-back" disabled={step === 0 || busy} onClick={goBack}
              className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 disabled:opacity-30 transition-colors">
              <ArrowLeft className="w-4 h-4" /> Back
            </button>

            <div className="flex items-center gap-3">
              {(step === 1 || step === 2) && (
                <button data-testid="onboarding-skip-step" disabled={busy} onClick={goNext}
                  className="text-sm font-semibold text-slate-500 hover:text-slate-900 transition-colors">
                  {step === 2 ? "I'll connect later" : "Skip"}
                </button>
              )}
              {step === 3 && (
                <button data-testid="onboarding-skip-dashboard" disabled={busy} onClick={skipToDashboard}
                  className="text-sm font-semibold text-slate-500 hover:text-slate-900 transition-colors">
                  I'll do this later
                </button>
              )}

              {step < STEPS.length - 1 ? (
                <button data-testid="onboarding-next" disabled={busy} onClick={goNext}
                  className="inline-flex items-center gap-2 bg-[#1D6AFF] hover:bg-[#1552C6] disabled:opacity-60 text-white font-semibold rounded-xl px-6 py-3 transition-all shadow-[0_14px_30px_-8px_rgba(29,106,255,0.45)] hover:-translate-y-0.5">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>{step === 1 ? "Save & Continue" : "Continue"} <ArrowRight className="w-4 h-4" /></>}
                </button>
              ) : (
                <button data-testid="onboarding-finish" disabled={busy} onClick={completeAndGo}
                  className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-60 text-white font-bold rounded-xl px-6 py-3 transition-all shadow-[0_14px_30px_-8px_rgba(16,185,129,0.45)] hover:-translate-y-0.5">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Sparkles className="w-4 h-4" /> Create My First Campaign</>}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <p className="text-center text-xs text-slate-400 mt-6 flex items-center justify-center gap-1.5">
        <ShieldCheck className="w-3 h-3" /> Your info is private and never shared with other agents.
      </p>
    </Shell>
  );
}

const Shell = ({ user, children }) => {
  const isElite = user.plan === "elite";
  return (
  <div className="min-h-screen relative overflow-hidden bg-slate-50 text-slate-900" data-testid="onboarding-page">
    <div className="absolute inset-0 bg-grid mask-radial-fade opacity-30 pointer-events-none" />
    <div className="absolute -top-40 -left-40 w-[520px] h-[520px] bg-[#1D6AFF]/15 rounded-full blur-[140px] pointer-events-none" />
    <div className="relative max-w-2xl mx-auto px-5 pt-10 pb-16">
      <div className="flex items-center justify-between mb-9">
        <Logo size="md" />
        {isElite ? (
          <div data-testid="plan-badge-elite" className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-1">
            <Crown className="w-3 h-3" /> Elite member
          </div>
        ) : (
          <div data-testid="plan-badge-pro" className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-[#1552C6] bg-[#1D6AFF]/10 border border-[#1D6AFF]/25 rounded-full px-2.5 py-1">
            <Sparkles className="w-3 h-3" /> Pro member
          </div>
        )}
      </div>
      {children}
    </div>
  </div>
  );
};

const Field = ({ icon: Icon, label, value, onChange, type = "text", placeholder, testId }) => (
  <div>
    <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2">{label}</label>
    <div className="relative flex items-center bg-slate-50 border border-slate-200 rounded-xl px-4 py-3.5 focus-within:border-[#1D6AFF] focus-within:bg-white transition-all">
      {Icon && <Icon className="w-4 h-4 text-slate-400 mr-3 shrink-0" />}
      <input data-testid={testId} type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        className="flex-1 bg-transparent outline-none text-slate-900 placeholder:text-slate-400 text-sm" />
    </div>
  </div>
);

// ── STEP 1: BUSINESS ─────────────────────────────────────────────────
const StepBusiness = ({ form, setForm }) => (
  <div className="space-y-4">
    <Field icon={User} testId="ob-name" label="Full name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} placeholder="Jane Carter" />
    <div>
      <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2">Account type</label>
      <div className="grid grid-cols-3 gap-2">
        {ACCOUNT_TYPES.map((t) => (
          <button key={t.value} type="button" data-testid={`ob-account-${t.value}`} onClick={() => setForm({ ...form, account_type: t.value })}
            className={`rounded-xl px-3 py-3 text-sm font-semibold border transition-all ${form.account_type === t.value ? "border-[#1D6AFF] bg-[#1D6AFF]/5 text-[#1552C6] ring-2 ring-[#1D6AFF]/15" : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"}`}>
            {t.label}
          </button>
        ))}
      </div>
    </div>
    <Field icon={Building2} testId="ob-brokerage" label="Brokerage name" value={form.brokerage_name} onChange={(v) => setForm({ ...form, brokerage_name: v })} placeholder="Sunset Realty" />
    <CityAutocomplete value={form.target_city} onChange={(v) => setForm({ ...form, target_city: v })} />
  </div>
);

// ── STEP 2: BRAND ────────────────────────────────────────────────────
const StepBrand = ({ form, setForm }) => {
  const headshotRef = useRef(null);
  const logoRef = useRef(null);
  const onPick = async (e, key) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try { const d = await readFileAsDataUrl(f, key === "headshot" ? 640 : 1024); setForm({ ...form, [key]: d }); }
    catch (_e) { /* ignore */ }
    if (e.target) e.target.value = "";
  };
  const first = form.name?.split(" ")[0] || "your agent";
  return (
    <div className="grid md:grid-cols-2 gap-6">
      <div className="space-y-5">
        <div>
          <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2">Headshot (optional)</label>
          <div className="flex items-center gap-3">
            <div className="w-16 h-16 rounded-full bg-slate-100 border border-slate-200 overflow-hidden flex items-center justify-center shrink-0">
              {form.headshot ? <img src={form.headshot} alt="" className="w-full h-full object-cover" /> : <User className="w-6 h-6 text-slate-300" />}
            </div>
            <button type="button" data-testid="ob-headshot-upload" onClick={() => headshotRef.current?.click()}
              className="inline-flex items-center gap-2 bg-slate-50 hover:bg-slate-100 border border-slate-200 text-slate-700 text-sm font-semibold rounded-xl px-4 py-2.5 transition-all">
              <Upload className="w-4 h-4" /> {form.headshot ? "Replace" : "Upload"}
            </button>
            <input ref={headshotRef} type="file" accept="image/*" className="hidden" onChange={(e) => onPick(e, "headshot")} />
          </div>
        </div>
        <div>
          <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2">Brokerage logo (optional)</label>
          <div className="flex items-center gap-3">
            <div className="w-16 h-16 rounded-xl bg-slate-100 border border-slate-200 overflow-hidden flex items-center justify-center shrink-0">
              {form.brand_logo ? <img src={form.brand_logo} alt="" className="w-full h-full object-contain" /> : <ImageIcon className="w-6 h-6 text-slate-300" />}
            </div>
            <button type="button" data-testid="ob-logo-upload" onClick={() => logoRef.current?.click()}
              className="inline-flex items-center gap-2 bg-slate-50 hover:bg-slate-100 border border-slate-200 text-slate-700 text-sm font-semibold rounded-xl px-4 py-2.5 transition-all">
              <Upload className="w-4 h-4" /> {form.brand_logo ? "Replace" : "Upload"}
            </button>
            <input ref={logoRef} type="file" accept="image/*" className="hidden" onChange={(e) => onPick(e, "brand_logo")} />
          </div>
        </div>
        <Field icon={Phone} testId="ob-biz-phone" label="Business phone (public)" value={form.business_phone} onChange={(v) => setForm({ ...form, business_phone: v })} placeholder="(305) 555-0123" />
        <Field icon={Mail} testId="ob-biz-email" label="Business email (public)" value={form.business_email} onChange={(v) => setForm({ ...form, business_email: v })} placeholder="jane@sunsetrealty.com" />
        <div>
          <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2 inline-flex items-center gap-1.5"><Palette className="w-3.5 h-3.5" /> Brand color</label>
          <div className="grid grid-cols-6 gap-2">
            {COLOR_PRESETS.map((p) => {
              const selected = form.brand_color_primary === p.primary && form.brand_color_accent === p.accent;
              return (
                <button key={p.name} type="button" data-testid={`ob-color-${p.name.toLowerCase()}`}
                  onClick={() => setForm({ ...form, brand_color_primary: p.primary, brand_color_accent: p.accent })}
                  className={`relative rounded-lg h-10 border transition-all ${selected ? "border-[#1D6AFF] ring-2 ring-[#1D6AFF]/20" : "border-slate-200 hover:border-slate-300"}`}
                  style={{ background: p.primary }} title={p.name}>
                  {selected && <div className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-[#1D6AFF] text-white flex items-center justify-center"><Check className="w-3 h-3" /></div>}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* LIVE ad mock preview */}
      <div>
        <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2">Live ad preview</label>
        <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-sm bg-slate-900" data-testid="brand-live-preview">
          <div className="relative h-48 bg-gradient-to-br from-slate-700 to-slate-900 flex items-center justify-center">
            <Home className="w-10 h-10 text-white/20" />
            <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/80 to-transparent" />
            {form.brand_logo && <img src={form.brand_logo} alt="" className="absolute top-3 left-3 h-8 max-w-[90px] object-contain drop-shadow" />}
            <div className="absolute bottom-3 left-3 right-3">
              <div className="text-white font-display text-lg font-bold leading-tight drop-shadow">Just Listed in {form.target_city?.split(",")[0] || "your market"}</div>
            </div>
          </div>
          <div className="bg-white p-3 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full overflow-hidden bg-slate-100 border border-slate-200 flex items-center justify-center shrink-0">
              {form.headshot ? <img src={form.headshot} alt="" className="w-full h-full object-cover" /> : <User className="w-5 h-5 text-slate-300" />}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-bold text-slate-900 truncate">{form.name || "Your Name"}</div>
              <div className="text-[11px] text-slate-500 truncate">{form.brokerage_name || "Your Brokerage"}{form.business_phone ? ` · ${form.business_phone}` : ""}</div>
            </div>
            <button type="button" className="text-xs font-bold text-white rounded-lg px-3 py-2 shrink-0" style={{ background: form.brand_color_primary }}>
              Talk to {first}
            </button>
          </div>
        </div>
        <p className="text-[11px] text-slate-400 mt-3 leading-relaxed">
          Saved as your <span className="font-semibold text-slate-500">Brand Kit</span> — the Creative Maker and Lead Engine reuse it automatically. Skip anything and we'll use a clean default.
        </p>
      </div>
    </div>
  );
};

// ── STEP 3: CONNECT META (honest, not simulated) ──────────────────────
const StepConnect = () => {
  const [showStatus, setShowStatus] = useState(false);
  return (
    <div>
      <div className="relative bg-gradient-to-br from-slate-50 to-white border border-slate-200 rounded-2xl p-6 sm:p-7 mb-5">
        <div className="flex items-center gap-4 mb-4">
          <div className="w-14 h-14 rounded-2xl bg-[#1877F2] flex items-center justify-center shrink-0 shadow-[0_10px_30px_-8px_rgba(24,119,242,0.45)]">
            <svg viewBox="0 0 24 24" className="w-7 h-7 text-white" fill="currentColor">
              <path d="M22.675 0h-21.35C.598 0 0 .598 0 1.337v21.326C0 23.402.598 24 1.325 24H12.82v-9.294H9.692V11.07h3.128V8.412c0-3.1 1.893-4.788 4.659-4.788 1.325 0 2.464.099 2.795.143v3.24h-1.918c-1.503 0-1.794.715-1.794 1.763v2.31h3.587l-.467 3.636h-3.12V24h6.116c.73 0 1.325-.598 1.325-1.337V1.337C24 .598 23.405 0 22.675 0z"/>
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-display text-xl font-bold text-slate-900 mb-1">Connect Facebook & Instagram</h3>
            <p className="text-sm text-slate-500">Authorize once to launch and manage campaigns from Newground. We never ask for your password.</p>
          </div>
        </div>
        <button type="button" data-testid="connect-meta-btn" onClick={() => setShowStatus(true)}
          className="w-full inline-flex items-center justify-center gap-2 bg-[#1877F2] hover:bg-[#1466d6] text-white font-semibold rounded-xl px-6 py-3.5 transition-all">
          <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor"><path d="M22.675 0h-21.35C.598 0 0 .598 0 1.337v21.326C0 23.402.598 24 1.325 24H12.82v-9.294H9.692V11.07h3.128V8.412c0-3.1 1.893-4.788 4.659-4.788 1.325 0 2.464.099 2.795.143v3.24h-1.918c-1.503 0-1.794.715-1.794 1.763v2.31h3.587l-.467 3.636h-3.12V24h6.116c.73 0 1.325-.598 1.325-1.337V1.337C24 .598 23.405 0 22.675 0z"/></svg>
          Connect with Meta
        </button>
        {showStatus && (
          <div className="mt-4 flex items-start gap-2.5 text-sm bg-amber-50 border border-amber-200 text-amber-800 rounded-xl px-4 py-3" data-testid="meta-status">
            <ShieldCheck className="w-4 h-4 mt-0.5 shrink-0" />
            <span>Meta connection isn't configured on this workspace yet. You can continue now and connect later from <span className="font-semibold">Settings</span> — your campaigns will save as drafts until Meta is linked.</span>
          </div>
        )}
      </div>
      <p className="text-xs text-slate-500 leading-relaxed">
        You can skip this and still create ads and prepare campaign drafts. Publishing to Facebook/Instagram becomes available once a real Meta connection is complete.
      </p>
    </div>
  );
};

// ── STEP 4: CHOOSE CAMPAIGN ───────────────────────────────────────────
const StepCampaign = ({ isElite, objective, setObjective, listingUrl, setListingUrl, manualListing, setManualListing, metaConnected }) => (
  <div className="space-y-4" data-testid="campaign-step">
    <div className={`flex items-center gap-2 text-xs font-semibold rounded-xl px-3 py-2 border ${metaConnected ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-slate-50 border-slate-200 text-slate-500"}`} data-testid="meta-connection-status">
      <span className={`w-2 h-2 rounded-full ${metaConnected ? "bg-emerald-500" : "bg-slate-300"}`} />
      {metaConnected ? "Meta connected — your campaign can publish after review." : "Meta not connected — you can build a draft; connect Meta to publish."}
    </div>
    {/* Promote a Listing */}
    <div role="button" tabIndex={0} data-testid="campaign-listing" onClick={() => setObjective("listing")}
      className={`w-full text-left rounded-2xl border p-5 transition-all cursor-pointer ${objective === "listing" ? "border-[#1D6AFF] ring-2 ring-[#1D6AFF]/15 bg-[#1D6AFF]/5" : "border-slate-200 hover:border-slate-300 bg-white"}`}>
      <div className="flex items-start gap-4">
        <div className="w-11 h-11 rounded-xl bg-[#1D6AFF]/10 border border-[#1D6AFF]/20 flex items-center justify-center shrink-0"><Home className="w-5 h-5 text-[#1D6AFF]" /></div>
        <div className="flex-1">
          <div className="font-display text-lg font-bold text-slate-900">Promote a Listing</div>
          <p className="text-sm text-slate-500">Turn your property listing into an advertisement.</p>
        </div>
        {objective === "listing" && <Check className="w-5 h-5 text-[#1D6AFF]" />}
      </div>
      {objective === "listing" && (
        <div className="mt-4" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center bg-white border border-slate-200 rounded-xl px-3 focus-within:border-[#1D6AFF]">
            <Link2 className="w-4 h-4 text-slate-400 shrink-0" />
            <input data-testid="campaign-listing-url" value={listingUrl} onChange={(e) => setListingUrl(e.target.value)}
              placeholder="Paste your listing URL (Zillow, Redfin, ...)"
              className="flex-1 bg-transparent outline-none text-sm text-slate-900 placeholder:text-slate-400 px-2 py-3" />
          </div>
          <button type="button" data-testid="campaign-listing-manual" onClick={() => setManualListing((m) => !m)}
            className="mt-2 text-xs font-semibold text-slate-500 hover:text-slate-900">
            {manualListing ? "✓ I'll enter details & photos manually" : "No link? Enter property details manually"}
          </button>
        </div>
      )}
    </div>

    {/* Attract Seller Leads — Elite */}
    <button type="button" data-testid="campaign-seller" onClick={() => { if (isElite) setObjective("seller"); }}
      className={`w-full text-left rounded-2xl border p-5 transition-all ${objective === "seller" ? "border-amber-400 ring-2 ring-amber-200 bg-amber-50/60" : "border-slate-200 hover:border-slate-300 bg-white"} ${!isElite ? "cursor-default" : ""}`}>
      <div className="flex items-start gap-4">
        <div className="w-11 h-11 rounded-xl bg-amber-100 border border-amber-200 flex items-center justify-center shrink-0"><TrendingUp className="w-5 h-5 text-amber-600" /></div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <div className="font-display text-lg font-bold text-slate-900">Attract Seller Leads</div>
            <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase bg-amber-100 text-amber-700 border border-amber-200 rounded-full px-2 py-0.5"><Crown className="w-2.5 h-2.5" /> Elite</span>
          </div>
          <p className="text-sm text-slate-500">Generate leads from homeowners interested in selling. No listing link needed — uses your market & branding.</p>
          {!isElite && (
            <div className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1" data-testid="seller-locked">
              <Lock className="w-3 h-3" /> Upgrade to Elite to unlock seller campaigns
            </div>
          )}
        </div>
        {objective === "seller" && isElite && <Check className="w-5 h-5 text-amber-600" />}
      </div>
    </button>
  </div>
);

